import React, { useState, useEffect } from 'react';
import { leaveService } from '../../../services/leave.service';
import type { LeaveType as LeaveTypeImport } from '../../../services/leave.service';

type LeaveType = LeaveTypeImport & {
  maxDaysPerRequest: number;
  requiresDocument: boolean;
  allowCarryOver?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

const LeaveTypeManagement: React.FC = () => {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType | null>(null);
  const [editingLeaveType, setEditingLeaveType] = useState<LeaveType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    color: '#3b82f6',
    defaultDaysPerYear: 10,
    maxDaysPerYear: 10,
    maxDaysPerRequest: 10,
    requiresApproval: true,
    requiresDocument: false,
    allowHalfDay: true,
    advanceNoticeDays: 0,
    isPaid: true,
    isActive: true,
    allowCarryOver: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const response = await leaveService.getAllLeaveTypes(true);
      setLeaveTypes(response.leaveTypes as LeaveType[]);
    } catch (error) {
      console.error('Error fetching leave types:', error);
      alert('Failed to load leave types');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      color: '#3b82f6',
      defaultDaysPerYear: 10,
      maxDaysPerYear: 10,
      maxDaysPerRequest: 10,
      requiresApproval: true,
      requiresDocument: false,
      allowHalfDay: true,
      advanceNoticeDays: 0,
      isPaid: true,
      isActive: true,
      allowCarryOver: false,
    });
    setShowCreateModal(true);
  };

  const handleEdit = (leaveType: LeaveType) => {
    setEditingLeaveType(leaveType);
    setFormData({
      name: leaveType.name,
      code: leaveType.code,
      description: leaveType.description || '',
      color: leaveType.color,
      defaultDaysPerYear: leaveType.defaultDaysPerYear || 0,
      maxDaysPerYear: leaveType.maxDaysPerYear || 0,
      maxDaysPerRequest: leaveType.maxDaysPerRequest || 0,
      requiresApproval: leaveType.requiresApproval,
      requiresDocument: leaveType.requiresDocument,
      allowHalfDay: leaveType.allowHalfDay,
      advanceNoticeDays: leaveType.minDaysNotice || 0,
      isPaid: leaveType.isPaid,
      isActive: leaveType.isActive ?? true,
      allowCarryOver: leaveType.allowCarryOver || false,
    });
    setShowEditModal(true);
  };

  const handleView = (leaveType: LeaveType) => {
    setSelectedLeaveType(leaveType);
    setShowViewModal(true);
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await leaveService.createLeaveType(formData);
      alert('Leave type created successfully!');
      setShowCreateModal(false);
      fetchLeaveTypes();
    } catch (error: any) {
      console.error('Error creating leave type:', error);
      alert(error.response?.data?.error?.message || 'Failed to create leave type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLeaveType) return;

    setSubmitting(true);

    try {
      await leaveService.updateLeaveType(editingLeaveType.id, formData);
      alert('Leave type updated successfully!');
      setShowEditModal(false);
      setEditingLeaveType(null);
      fetchLeaveTypes();
    } catch (error: any) {
      console.error('Error updating leave type:', error);
      alert(error.response?.data?.error?.message || 'Failed to update leave type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to deactivate "${name}"? This action will soft-delete the leave type.`)) {
      return;
    }

    try {
      await leaveService.deleteLeaveType(id);
      alert('Leave type deactivated successfully!');
      fetchLeaveTypes();
    } catch (error: any) {
      console.error('Error deleting leave type:', error);
      alert(error.response?.data?.error?.message || 'Failed to delete leave type');
    }
  };

  const handleToggleActive = async (leaveType: LeaveType) => {
    const newStatus = !leaveType.isActive;
    try {
      await leaveService.updateLeaveType(leaveType.id, { isActive: newStatus });
      alert(`Leave type ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchLeaveTypes();
    } catch (error: any) {
      console.error('Error toggling leave type status:', error);
      alert(error.response?.data?.error?.message || 'Failed to update leave type status');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading leave types...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">ประเภทการลาทั้งหมด</h3>
          <p className="text-sm text-gray-600">จัดการประเภทการลาต่างๆ ในระบบ</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          สร้างประเภทการลาใหม่
        </button>
      </div>

      {/* Leave Types Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ประเภทการลา
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  รหัส
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  วันลาต่อปี
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ต้องอนุมัติ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  สถานะ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  การจัดการ
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leaveTypes.map((leaveType) => (
                <tr key={leaveType.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full mr-3" 
                        style={{ backgroundColor: leaveType.color }}
                      ></div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {leaveType.icon} {leaveType.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {leaveType.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {leaveType.code}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {leaveType.defaultDaysPerYear} / {leaveType.maxDaysPerYear} วัน
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      leaveType.requiresApproval
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {leaveType.requiresApproval ? 'ต้องอนุมัติ' : 'อนุมัติอัตโนมัติ'}
                    </span>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(leaveType)}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 ${
                        leaveType.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {leaveType.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </button>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                    <button 
                      onClick={() => handleView(leaveType)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      ดู
                    </button>
                    <button 
                      onClick={() => handleEdit(leaveType)}
                      className="text-green-600 hover:text-green-900"
                    >
                      แก้ไข
                    </button>
                    {leaveType.isActive && (
                      <button 
                        onClick={() => handleDelete(leaveType.id, leaveType.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        ลบ
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">สร้างประเภทการลาใหม่</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ชื่อประเภทการลา *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="เช่น ลาป่วย, ลากิจ"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    รหัส *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="เช่น SICK, ANNUAL"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    คำอธิบาย
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="อธิบายรายละเอียดเพิ่มเติม..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    สี
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    วันลาเริ่มต้นต่อปี *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.defaultDaysPerYear}
                    onChange={(e) => setFormData({ ...formData, defaultDaysPerYear: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    วันลาสูงสุดต่อปี *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.maxDaysPerYear}
                    onChange={(e) => setFormData({ ...formData, maxDaysPerYear: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    วันลาสูงสุดต่อครั้ง *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.maxDaysPerRequest}
                    onChange={(e) => setFormData({ ...formData, maxDaysPerRequest: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    แจ้งล่วงหน้า (วัน)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.advanceNoticeDays}
                    onChange={(e) => setFormData({ ...formData, advanceNoticeDays: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">ต้องอนุมัติ</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.requiresDocument}
                    onChange={(e) => setFormData({ ...formData, requiresDocument: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">ต้องแนบเอกสาร</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allowHalfDay}
                    onChange={(e) => setFormData({ ...formData, allowHalfDay: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">อนุญาตลาครึ่งวัน</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPaid}
                    onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">ลาแบบมีเงินเดือน</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allowCarryOver}
                    onChange={(e) => setFormData({ ...formData, allowCarryOver: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">อนุญาตสะสมวันลา</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">เปิดใช้งาน</span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'กำลังสร้าง...' : 'สร้างประเภทการลา'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Same as Create but with pre-filled data **/}
      {showEditModal && editingLeaveType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">แก้ไขประเภทการลา</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingLeaveType(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ชื่อประเภทการลา *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    รหัส *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    คำอธิบาย
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    สี
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    วันลาเริ่มต้นต่อปี *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.defaultDaysPerYear}
                    onChange={(e) => setFormData({ ...formData, defaultDaysPerYear: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    วันลาสูงสุดต่อปี *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.maxDaysPerYear}
                    onChange={(e) => setFormData({ ...formData, maxDaysPerYear: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    วันลาสูงสุดต่อครั้ง *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.maxDaysPerRequest}
                    onChange={(e) => setFormData({ ...formData, maxDaysPerRequest: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    แจ้งล่วงหน้า (วัน)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.advanceNoticeDays}
                    onChange={(e) => setFormData({ ...formData, advanceNoticeDays: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">ต้องอนุมัติ</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.requiresDocument}
                    onChange={(e) => setFormData({ ...formData, requiresDocument: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">ต้องแนบเอกสาร</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allowHalfDay}
                    onChange={(e) => setFormData({ ...formData, allowHalfDay: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">อนุญาตลาครึ่งวัน</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPaid}
                    onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">ลาแบบมีเงินเดือน</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.allowCarryOver}
                    onChange={(e) => setFormData({ ...formData, allowCarryOver: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">อนุญาตสะสมวันลา</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">เปิดใช้งาน</span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingLeaveType(null);
                  }}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'กำลังบันทึก...' : 'บันทึกการเปลี่ยนแปลง'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedLeaveType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">รายละเอียดประเภทการลา</h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedLeaveType(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                  style={{ backgroundColor: selectedLeaveType.color + '20' }}
                >
                  {selectedLeaveType.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedLeaveType.name}</h3>
                  <p className="text-sm text-gray-600">รหัส: {selectedLeaveType.code}</p>
                </div>
              </div>
              
              {selectedLeaveType.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">คำอธิบาย</h4>
                  <p className="text-gray-900">{selectedLeaveType.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">วันลาเริ่มต้นต่อปี</h4>
                  <p className="text-lg font-semibold">{selectedLeaveType.defaultDaysPerYear} วัน</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">วันลาสูงสุดต่อปี</h4>
                  <p className="text-lg font-semibold">{selectedLeaveType.maxDaysPerYear} วัน</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">วันลาสูงสุดต่อครั้ง</h4>
                  <p className="text-lg font-semibold">{selectedLeaveType.maxDaysPerRequest} วัน</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">แจ้งล่วงหน้า</h4>
                  <p className="text-lg font-semibold">{selectedLeaveType.minDaysNotice || 0} วัน</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${selectedLeaveType.requiresApproval ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">ต้องอนุมัติ</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${selectedLeaveType.requiresDocument ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">ต้องแนบเอกสาร</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${selectedLeaveType.allowHalfDay ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">อนุญาตลาครึ่งวัน</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${selectedLeaveType.isPaid ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">ลาแบบมีเงินเดือน</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${selectedLeaveType.allowCarryOver ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">อนุญาตสะสมวันลา</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${selectedLeaveType.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm">{selectedLeaveType.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}</span>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
                {selectedLeaveType.createdAt && (
                  <p>สร้างเมื่อ: {new Date(selectedLeaveType.createdAt).toLocaleString('th-TH')}</p>
                )}
                {selectedLeaveType.updatedAt && (
                  <p>แก้ไขล่าสุด: {new Date(selectedLeaveType.updatedAt).toLocaleString('th-TH')}</p>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedLeaveType(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                ปิด
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(selectedLeaveType);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                แก้ไข
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypeManagement;
